/**
 * A very small Markdown renderer.
 *
 * The assistant answers in Markdown, because that is what language models
 * write whether you ask for it or not — leaving it unrendered means users read
 * literal `**asterisks**` and numbered lists that never indent.
 *
 * Written here rather than pulled in because APPROVED_LIBRARIES.md is a closed
 * list and this needs a fraction of what a real parser does: the subset a
 * chat answer actually uses.
 *
 * Security: every character is escaped BEFORE any tag is produced, so nothing
 * in the input can become markup. The only tags in the output are the ones
 * this file writes, and the only attribute is an href that had to survive
 * safeUrl(). Model output is untrusted — it can quote whatever a user typed.
 */

import { DIAGRAMS, renderDiagram } from './diagram.js'

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

function escapeHtml(text) {
  // NULs are stripped, not escaped: the code-span placeholder below uses them
  // as a delimiter, and one arriving in the input would let a message forge it.
  return String(text).replace(/\0/g, '').replace(/[&<>"']/g, char => ESCAPES[char])
}

/** Only protocols that cannot execute. `javascript:` and `data:` are dropped. */
function safeUrl(url) {
  const trimmed = String(url).trim()
  return /^(https?:\/\/|mailto:|tel:|\/)/i.test(trimmed) ? trimmed : null
}

const UNESCAPES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" }

/** An href as it was written, for whoever decides whether it may be followed. */
function unescapeHtml(text) {
  return String(text).replace(/&(?:amp|lt|gt|quot|#39);/g, entity => UNESCAPES[entity])
}

/**
 * Inline marks, applied to already-escaped text.
 *
 * @param {Function|null} isTrusted  see renderMarkdown
 */
function inline(text, isTrusted = null) {
  // Code spans are lifted out before anything else runs. Replacing them in
  // place would not protect them: `**not bold**` inside backticks would still
  // be sitting in the string when the bold rule swept through.
  const spans = []
  const withoutCode = text.replace(/`([^`]+)`/g, (_, code) => {
    spans.push(code)
    return `\0${spans.length - 1}\0`
  })

  const marked = withoutCode
    // [label](url)
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label, url) => {
      const href = safeUrl(url)
      if (!href) return label
      // Decided HERE, on the same text this function draws links from, after
      // the code has been lifted out exactly as it will be shown. Deciding it
      // on the Markdown beforehand —splitting it by every backtick— read a
      // lone backtick earlier in the answer as the start of code, and the link
      // after it was left alive (2026-10-21).
      if (isTrusted && !isTrusted(unescapeHtml(href))) {
        const shown = label.trim()
        const plain = href.replace(/^(mailto|tel):/i, '')
        return shown === href || shown === plain ? shown : `${shown} (${href})`
      }
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
    })
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/(^|[\s(])_([^_\n]+)_/g, '$1<em>$2</em>')

  return marked.replace(/\0(\d+)\0/g, (_, index) => `<code>${spans[Number(index)]}</code>`)
}

/**
 * @param {string} source
 * @param {object} [options]
 * @param {boolean} [options.hardBreaks=true]  si un salto de linea suelto es
 *   un salto en pantalla.
 *
 *   Verdadero por defecto porque el usuario original de esto es el asistente,
 *   y ahi los saltos son intencionados: el modelo escribe una linea por idea.
 *
 *   Falso es lo que quiere un DOCUMENTO. La ayuda de un modulo se escribe a
 *   ochenta columnas —para que el fichero se lea en el repositorio y en el
 *   diff— y con saltos duros esos ochenta caracteres se convertian en el
 *   ancho del texto en pantalla: los renglones se cortaban a mitad de la
 *   columna y la mitad derecha quedaba vacia, mirara donde mirara el lector.
 *   Es lo que hace Markdown de toda la vida: un salto suelto refluye, y el
 *   parrafo lo parte la pantalla.
 * @param {Function} [options.isTrusted]  `(href) => boolean`: si un enlace se
 *   puede seguir. El que no, sale como texto («etiqueta (dirección)»). Sin él,
 *   todo enlace con un protocolo seguro se dibuja, como siempre. Se aplica
 *   DENTRO del renderizado, después de apartar el código, para que lo que se
 *   filtra sea exactamente lo que se dibuja.
 * @returns {string} HTML, safe to hand to v-html
 */
export function renderMarkdown(source, { hardBreaks = true, isTrusted = null } = {}) {
  if (!source) return ''

  // `\r\n` y `\r` sueltos pasan a `\n` antes de nada. Un `.md` sacado de git en
  // Windows llega con finales CRLF, y el `\r` que se queda al final de cada línea
  // hace que ningún `## título`, viñeta ni valla case: la ayuda de tres módulos
  // salía entera como texto crudo, con las almohadillas y los asteriscos a la
  // vista, y sin un error en ninguna parte.
  const lines = escapeHtml(String(source).replace(/\r\n?/g, '\n')).split('\n')
  const html = []

  let listType = null      // 'ul' | 'ol' | null
  let inCodeBlock = false
  // El esquema que se esta leyendo: su tipo y las lineas que lleva dentro.
  // Un esquema es una valla CON NOMBRE (```pasos), asi que se acumula igual
  // que un bloque de codigo y se dibuja al cerrarla.
  let diagram = null
  let diagramLines = []
  let paragraph = []

  // La viñeta que se está escribiendo, mientras se escribe.
  //
  // Se acumula en vez de cerrarse en cuanto aparece porque una viñeta puede
  // ocupar varias líneas: es Markdown normal (la «continuación perezosa») y es
  // como se escribe un texto largo, que se parte a los ochenta caracteres.
  //
  // Sin esto, la segunda línea salía como un párrafo APARTE —fuera de la lista,
  // con su sangría perdida— y, peor, una negrita partida por el salto se
  // quedaba con los asteriscos puestos: `inline()` ve una línea cada vez y para
  // ella `**un texto` no abre nada. En una lista de cinco viñetas largas eso
  // son cinco párrafos huérfanos.
  let item = null

  const closeParagraph = () => {
    if (paragraph.length) {
      html.push(`<p>${inline(paragraph.join(hardBreaks ? '<br>' : ' '), isTrusted)}</p>`)
      paragraph = []
    }
  }
  const closeItem = () => {
    if (item) {
      // Con espacio y no con `<br>`: una viñeta partida por el ancho de la
      // pantalla es UNA frase, y un salto ahí lo dibujaría donde el autor sólo
      // llegó al margen.
      html.push(`<li>${inline(item.join(' '), isTrusted)}</li>`)
      item = null
    }
  }
  const closeList = () => {
    closeItem()
    if (listType) {
      html.push(`</${listType}>`)
      listType = null
    }
  }
  const openList = (type) => {
    if (listType !== type) {
      closeList()
      html.push(`<${type}>`)
      listType = type
    }
  }

  for (const line of lines) {
    // ── Fenced code ──────────────────────────────────────────────────────────
    //
    // La valla puede llevar nombre (```pasos). Si es uno de los que se saben
    // dibujar, lo de dentro se acumula y sale como un ESQUEMA; cualquier otro
    // nombre --o ninguno-- sigue siendo un bloque de codigo, como siempre.
    const fence = line.match(/^\s*```\s*([a-zA-Z]*)\s*$/)
    if (fence) {
      if (diagram) {
        html.push(renderDiagram(diagram, diagramLines))
        diagram = null
        diagramLines = []
        continue
      }
      if (inCodeBlock) {
        html.push('</code></pre>')
        inCodeBlock = false
        continue
      }

      closeParagraph()
      closeList()

      if (DIAGRAMS.includes(fence[1])) {
        diagram = fence[1]
        diagramLines = []
      } else {
        html.push('<pre><code>')
        inCodeBlock = true
      }
      continue
    }
    if (diagram) {
      diagramLines.push(line)
      continue
    }
    if (inCodeBlock) {
      html.push(`${line}\n`)
      continue
    }

    // ── Blank line: ends whatever was open ───────────────────────────────────
    if (!line.trim()) {
      closeParagraph()
      closeList()
      continue
    }

    // ── Headings ─────────────────────────────────────────────────────────────
    const heading = line.match(/^(#{1,4})\s+(.*)$/)
    if (heading) {
      closeParagraph()
      closeList()
      // Shifted down two levels: an answer inside a panel must not claim to be
      // a page heading, and h1/h2 belong to the screen around it.
      const level = Math.min(heading[1].length + 2, 6)
      html.push(`<h${level}>${inline(heading[2], isTrusted)}</h${level}>`)
      continue
    }

    // ── Horizontal rule ──────────────────────────────────────────────────────
    if (/^\s*([-*_])\s*\1\s*\1[\s-*_]*$/.test(line)) {
      closeParagraph()
      closeList()
      html.push('<hr>')
      continue
    }

    // ── Lists ────────────────────────────────────────────────────────────────
    const bullet = line.match(/^\s*[-*+]\s+(.*)$/)
    if (bullet) {
      closeParagraph()
      closeItem()
      openList('ul')
      item = [bullet[1]]
      continue
    }

    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/)
    if (numbered) {
      closeParagraph()
      closeItem()
      openList('ol')
      item = [numbered[1]]
      continue
    }

    // ── Quote ────────────────────────────────────────────────────────────────
    const quote = line.match(/^\s*&gt;\s?(.*)$/)
    if (quote) {
      closeParagraph()
      closeList()
      html.push(`<blockquote>${inline(quote[1], isTrusted)}</blockquote>`)
      continue
    }

    // ── La continuación de una viñeta ────────────────────────────────────────
    //
    // Una línea suelta justo debajo de una viñeta es la MISMA viñeta partida
    // por el ancho del fichero. Cerrar la lista aquí —que es lo que se hacía—
    // la partía en dos cosas distintas.
    if (item) {
      item.push(line.trim())
      continue
    }

    // ── Anything else is a paragraph line ────────────────────────────────────
    closeList()
    paragraph.push(line.trim())
  }

  closeParagraph()
  closeList()
  if (inCodeBlock) html.push('</code></pre>')

  return html.join('')
}

export default renderMarkdown
